/**
 * PassportShareBar — WhatsApp / LinkedIn / X / Copy-link buttons for the
 * public passport. Points at the crawler-ready share-shell endpoint
 * (`/api/social-capital/share/passport/…`) so previews render the
 * Pillow-rendered OG card automatically, not the SPA default.
 */
import { useMemo, useState } from "react";
import { Share2, MessageCircle, Linkedin, Twitter, Link as LinkIcon, Check } from "lucide-react";
import { toast } from "sonner";

export default function PassportShareBar({ handle, name, points, testid = "passport-share-bar" }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => {
    // Prefer the crawler-friendly share endpoint served by the backend.
    // Social bots (Meta, LinkedIn, Slack, X) fetch this URL, read the OG
    // meta tags baked into the HTML shell, and pull the Pillow OG card.
    const base = process.env.REACT_APP_SOCIAL_CAPITAL_API_BASE
      || process.env.REACT_APP_BACKEND_URL
      || (typeof window !== "undefined" ? window.location.origin : "");
    return `${base.replace(/\/$/, "")}/api/social-capital/share/passport/${encodeURIComponent(handle)}`;
  }, [handle]);

  const blurb = useMemo(() => {
    const total = Number(points || 0).toLocaleString();
    return `${name}'s Social Capital passport — ${total} verified points on SocialCapital.cc`;
  }, [name, points]);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${blurb} ${shareUrl}`)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const xHref        = `https://twitter.com/intent/tweet?text=${encodeURIComponent(blurb)}&url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  const btnStyle = {
    background: "rgba(255,255,255,0.14)",
    color: "#F0FDF4",
    minHeight: 40,
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 mt-4"
      data-testid={testid}
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest opacity-80 mr-1">
        <Share2 className="w-3.5 h-3.5" /> Share
      </span>
      <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
         style={btnStyle} data-testid={`${testid}-whatsapp`}
         aria-label="Share on WhatsApp">
        <MessageCircle className="w-4 h-4" /> WhatsApp
      </a>
      <a href={linkedinHref} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
         style={btnStyle} data-testid={`${testid}-linkedin`}
         aria-label="Share on LinkedIn">
        <Linkedin className="w-4 h-4" /> LinkedIn
      </a>
      <a href={xHref} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
         style={btnStyle} data-testid={`${testid}-x`}
         aria-label="Share on X">
        <Twitter className="w-4 h-4" /> X
      </a>
      <button type="button" onClick={copyLink}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-transform hover:-translate-y-0.5"
              style={btnStyle} data-testid={`${testid}-copy`}
              aria-label="Copy share link">
        {copied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}
